// Nosto utilities are lazy-loaded so the search bar doesn't pull them onto every
// page; fetchNostoResults() (the only consumer) awaits this before use.
let nostoModulePromise = null;
function loadNostoUtilities() {
  if (!nostoModulePromise) {
    nostoModulePromise = import('@utilities/nosto');
  }
  return nostoModulePromise;
}
import { handleize, scrollLock, debounce, formatNostoErrorCode } from '@utilities/theme';

/**
 * @fileoverview Predictive search functionality for Shopify stores. This file contains two custom elements:
 * - predictive-search: Handles real-time search results and user interactions
 * - predictive-search-suggestions: Displays popular and recommended search suggestions
 */

/**
 * Builds a suggestion URL with tracking parameter.
 * Handles both direct URLs and fallback search URLs, and appends tracking parameters
 * for collection and search URLs.
 *
 * @param {Object} item - The item containing url and title/text properties
 * @param {string} [item.url] - The direct URL for the item (optional)
 * @param {string} [item.title] - The title to use for search fallback
 * @param {string} [item.text] - Alternative text property to use for search fallback (used by queries)
 * @returns {string} The formatted URL with tracking parameter if applicable
 */
function buildSuggestionUrl(item) {
  // Use url if provided, otherwise build a search URL from title or text
  const searchTerm = item.title || item.text || '';
  let suggestionUrl = item.url && item.url !== '' ? item.url : `/search?q=${encodeURIComponent(searchTerm)}`;

  // Determine the correct delimiter for adding query parameters
  const delimiter = suggestionUrl.includes('?') ? '&' : '?';

  // Add tracking parameter for collection and search URLs
  if (suggestionUrl.includes('/collections/') || suggestionUrl.includes('/search')) {
    suggestionUrl = `${suggestionUrl}${delimiter}is_search_suggestion=true`;
  }

  return suggestionUrl;
}

/**
 * @classdesc A custom element that provides predictive search functionality.
 * @extends HTMLElement
 */
class PredictiveSearch extends HTMLElement {
  /**
   * Creates a new PredictiveSearch instance.
   * @constructor
   */
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.productLimit = 5;
    this.cachedResults = {};
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.searchTimeout = null;
    this.pendingRequests = new Map();
    // GC-2374: set true once a Nosto search/trending request fails in this box, so later
    // searches skip Nosto and go straight to Shopify (local fallback; does not flip the page).
    this.nostoFailedThisPage = false;
    this.DEBOUNCE_MS = 250; // Slightly reduced from 300ms for better responsiveness
    this.CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes cache expiry
    this.productCardComponentTemplate = null;
    this.viewportHeightHandler = null;

    // Trending products configuration
    this.trendingConfig = {
      collectionId: null,
      collectionHandle: null,
      productsLimit: 5,
      heading: 'Trending Now',
      isLoaded: false,
      isLoading: false
    };
    this.trendingLoadPromise = null; // Track the loading promise
  }

  allowDebug() {
    return this.classList.contains("debug");
  }

  /**
   * Check if predictive search is currently open
   * @returns {boolean}
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  /**
   * Called when the element is connected to the DOM.
   * Initializes and renders the search form.
   */
  connectedCallback() {
    // Get references to DOM elements - header input (desktop) and mobile input (overlay)
    this.headerInput = document.querySelector('[data-header-search-input]');
    this.mobileInput = this.querySelector('[data-mobile-search-input]');
    this.mobileCloseButton = this.querySelector('[data-mobile-close-button]');

    // Use appropriate input based on screen size
    this.input = this.getActiveInput();
    this.queryInput = this.querySelector('[data-predictive-search-query-input]');
    this.resetButton = this.querySelector('button[type="reset"]');

    this.setupEventListeners();
    this.setupViewportHeightHandler();
    this.setupScrollKeyboardHandler();
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search-results]"
    );
    this.productCardComponentTemplate = this.querySelector(
      "template#predictive-search-product-card-component-template"
    );

    // Trending products setup
    this.parseTrendingConfig();

    // Focus header input when search modal opens
    this.setupHeaderInputFocus();
  }

  /**
   * Get the active input based on screen size
   * @returns {HTMLElement} The active input element
   * @private
   */
  getActiveInput() {
    const isMobile = window.innerWidth < 990;
    return isMobile ? this.mobileInput : this.headerInput;
  }

  /**
   * Parse trending products configuration from window.theme.search.trending
   * @private
   */
  parseTrendingConfig() {
    const trendingData = window.theme?.search?.trending;
    if (trendingData) {
      this.trendingConfig.collectionId = trendingData.collectionId || null;
      this.trendingConfig.collectionHandle = trendingData.collectionHandle || null;
      this.trendingConfig.productsLimit = trendingData.productsLimit || 5;
      this.trendingConfig.heading = trendingData.heading || 'Trending Now';

      if (this.allowDebug()) {
        console.log('PredictiveSearch: Trending config parsed:', this.trendingConfig);
      }
    } else if (this.allowDebug()) {
      console.log('PredictiveSearch: No trending config found');
    }
  }

  /**
   * Check if trending products are configured
   * @returns {boolean}
   */
  hasTrendingProducts() {
    return this.trendingConfig.collectionId !== null && this.trendingConfig.collectionHandle !== null;
  }

  /**
   * Called when the element is disconnected from the DOM.
   * Cleans up resources and aborts pending requests.
   */
  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    clearTimeout(this.searchTimeout);
    this.pendingRequests.clear();
    this.cleanupViewportHeightHandler();
    this.cleanupScrollKeyboardHandler();

    // Unlock body scroll if it was locked using global scrollLock
    scrollLock.disableBodyScrollLock();

    // Remove document-level event listeners
    if (this._inputHandlers) {
      document.removeEventListener('input', this._inputHandlers.input);
      document.removeEventListener('focus', this._inputHandlers.focus, true);
      document.removeEventListener('keyup', this._inputHandlers.keyup);
      document.removeEventListener('keydown', this._inputHandlers.keydown);
      this._inputHandlers = null;
    }
  }

  /**
   * Check if element is a search input
   * @private
   */
  isSearchInput(element) {
    return element?.matches('[data-header-search-input], [data-mobile-search-input]');
  }

  /**
   * Sets up event listeners using event delegation.
   * @private
   */
  setupEventListeners() {
    const debouncedOnChange = debounce((event) => {
      this.syncQueryInput();
      this.onChange(event);
    }, 300);

    // Document-level delegation for search inputs (handles both internal and external)
    const handleInput = (event) => {
      if (this.isSearchInput(event.target)) {
        debouncedOnChange(event);
      }
    };

    const handleFocus = (event) => {
      if (this.isSearchInput(event.target)) {
        this.onFocus(event);
      }
    };

    const handleKeyup = (event) => {
      if (this.isSearchInput(event.target)) {
        this.onKeyup(event);
      }
    };

    const handleKeydown = (event) => {
      if (this.isSearchInput(event.target)) {
        this.onKeydown(event);
      }
    };

    document.addEventListener('input', handleInput);
    document.addEventListener('focus', handleFocus, true);
    document.addEventListener('keyup', handleKeyup);
    document.addEventListener('keydown', handleKeydown);

    // Component-level delegation for internal elements
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-mobile-close-button]')) {
        event.preventDefault();
        this.closeSearchModal();
      }
    });

    this.addEventListener('submit', (event) => {
      if (event.target.matches('form')) {
        this.onFormSubmit(event);
      }
    });

    this.addEventListener('reset', (event) => {
      if (event.target.matches('form')) {
        this.onFormReset(event);
      }
    });

    this.addEventListener('focusout', this.onFocusOut.bind(this));

    // Listen for vendor changes from theme utilities
    document.addEventListener(
      "vendor:changed",
      this.handleVendorChange.bind(this)
    );

    // Handle window resize to switch active input
    window.addEventListener('resize', debounce(() => {
      this.input = this.getActiveInput();
    }, 150));

    // Store handlers for cleanup
    this._inputHandlers = {
      input: handleInput,
      focus: handleFocus,
      keyup: handleKeyup,
      keydown: handleKeydown
    };
  }

  /**
   * Sets up focus handling for the search inputs.
   * @private
   */
  setupHeaderInputFocus() {
    // Find the details element that contains the search modal
    const detailsElement = this.headerInput?.closest('details') || this.closest('details-modal')?.querySelector('details');
    if (!detailsElement) return;

    // Use MutationObserver to detect when details opens
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'open') {
          if (detailsElement.hasAttribute('open')) {
            // Lock scroll when modal opens using global scrollLock
            scrollLock.enabledBodyScrollLock();

            // Re-parse trending config in case it wasn't available during connectedCallback
            this.parseTrendingConfig();

            // Load trending products if configured and not already loading/loaded
            // Then show suggestions after loading completes
            if (this.hasTrendingProducts() && !this.trendingData && !this.trendingConfig.isLoading) {
              this.loadTrendingProducts().then(() => {
                // After trending loads, show suggestions if input is still empty
                if (!this.getQuery().length && detailsElement.hasAttribute('open')) {
                  this.showSuggestions();
                }
              });
            }

            // Focus the appropriate input when modal opens
            const activeInput = this.getActiveInput();
            setTimeout(() => {
              activeInput?.focus();
            }, 50);
          } else {
            // Unlock scroll when modal closes using global scrollLock
            scrollLock.disableBodyScrollLock();

            // Clear both inputs when modal closes
            if (this.headerInput) {
              this.headerInput.value = '';
            }
            if (this.mobileInput) {
              this.mobileInput.value = '';
            }
            this.syncQueryInput();
            this.searchTerm = '';
          }
        }
      });
    });

    observer.observe(detailsElement, { attributes: true });
  }

  /**
   * Closes the search modal overlay.
   * @private
   */
  closeSearchModal() {
    const detailsElement = this.headerInput?.closest('details') || this.closest('details-modal')?.querySelector('details');
    if (detailsElement) {
      detailsElement.removeAttribute('open');
    }
  }

  /**
   * Syncs the active input value with the hidden query input for form submission.
   * Also syncs both visible inputs to keep them in sync.
   * @private
   */
  syncQueryInput() {
    const activeInput = this.getActiveInput();

    // Sync to hidden input
    if (this.queryInput && activeInput) {
      this.queryInput.value = activeInput.value;
    }

    // Keep both inputs in sync (in case of resize during typing)
    if (this.headerInput && this.mobileInput) {
      const value = activeInput?.value || '';
      if (this.headerInput !== activeInput) this.headerInput.value = value;
      if (this.mobileInput !== activeInput) this.mobileInput.value = value;
    }
  }

  /**
   * Sets up viewport height handling for mobile keyboard compatibility.
   * @private
   */
  setupViewportHeightHandler() {
    // Only set up on mobile devices
    if (window.innerWidth >= 990) return;

    this.updateViewportHeight = () => {
      // Calculate the actual available height
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);

      // Also set a specific property for predictive search
      const availableHeight = window.innerHeight;
      document.documentElement.style.setProperty('--predictive-search-vh', `${availableHeight}px`);
    };

    // Set initial values
    this.updateViewportHeight();

    // Update on resize and orientation change
    this.viewportHeightHandler = this.updateViewportHeight.bind(this);
    window.addEventListener('resize', this.viewportHeightHandler);
    window.addEventListener('orientationchange', this.viewportHeightHandler);

    // Also listen for visual viewport changes (modern browsers)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.viewportHeightHandler);
    }
  }

  /**
   * Cleans up viewport height event listeners.
   * @private
   */
  cleanupViewportHeightHandler() {
    if (this.viewportHeightHandler) {
      window.removeEventListener('resize', this.viewportHeightHandler);
      window.removeEventListener('orientationchange', this.viewportHeightHandler);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.viewportHeightHandler);
      }

      this.viewportHeightHandler = null;
    }
  }

  /**
   * Sets up touch/scroll handler to hide keyboard on mobile when scrolling results.
   * Uses touchmove for better mobile reliability since scroll events can be delayed.
   * @private
   */
  setupScrollKeyboardHandler() {
    console.log('setupScrollKeyboardHandler called, width:', window.innerWidth);
    // Only set up on mobile/touch devices
    if (window.innerWidth >= 990) {
      return;
    }

    let scrollTimeout;

    this.handleResultsScroll = () => {
      console.log('Touch/scroll event detected');
      // Debounce to avoid excessive blur calls during fast scrolling
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // Get the active input element
        const activeInput = this.getActiveInput();

        // Only blur if the input is currently focused
        if (activeInput && document.activeElement === activeInput) {
          console.log('Blurring keyboard...');
          // Blur the input to hide the keyboard
          activeInput.blur();
        }
      }, 50);
    };

    this.addEventListener('touchmove', this.handleResultsScroll, { passive: true });

    // Also listen to scroll as a fallback for non-touch devices or mouse wheel
    this.addEventListener('scroll', this.handleResultsScroll, { passive: true });
  }

  /**
   * Cleans up touch/scroll keyboard event listeners.
   * @private
   */
  cleanupScrollKeyboardHandler() {
    if (this.handleResultsScroll) {
      this.removeEventListener('touchmove', this.handleResultsScroll);
      this.removeEventListener('scroll', this.handleResultsScroll);
      this.handleResultsScroll = null;
    }
  }

  /**
   * Gets the current search query from the active input field.
   * @returns {string} The trimmed search query
   */
  getQuery() {
    const activeInput = this.getActiveInput();
    return activeInput ? activeInput.value.trim() : '';
  }

  /**
   * Handles changes to the search input with debouncing.
   */
  onChange() {
    this.toggleResetButton();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // If input is focused and empty, show suggestions; otherwise close
      if (document.activeElement === this.input) {
        this.showSuggestions();
      } else {
        this.close(true);
      }
      return;
    }

    // Debounce search requests
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.getSearchResults(this.searchTerm);
    }, this.DEBOUNCE_MS);
  }

  /**
   * Toggle the visibility of the reset button based on input value.
   * Mobile close button is always visible, so only toggles form reset button.
   * @private
   */
  toggleResetButton() {
    // Toggle form reset button (if exists)
    if (this.resetButton) {
      const activeInput = this.getActiveInput();
      const hasValue = activeInput?.value.length > 0;

      if (hasValue) {
        this.resetButton.classList.remove('hidden');
      } else {
        this.resetButton.classList.add('hidden');
      }
    }
  }

  /**
   * Check if the form should be reset.
   * Integrated from SearchForm class.
   * @returns {boolean} Whether the form should be reset
   * @private
   */
  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  /**
   * Handles form submission.
   * @param {Event} event - The form submit event
   */
  onFormSubmit(event) {
    let redirectTo = "";
    if (window.theme.search.redirects.length > 0) {
      window.theme.search.redirects.forEach((value) => {
        if (
          handleize(value.title) ==
          handleize(this.getQuery())
        ) {
          redirectTo = value.url;
        }
      });
    }
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
    } else if (redirectTo !== "") {
      event.preventDefault();
      location.href = `${redirectTo}`;
    }
  }

  /**
   * Handles form reset.
   * @param {Event} event - The form reset event
   */
  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      // Clear both inputs
      if (this.headerInput) this.headerInput.value = '';
      if (this.mobileInput) this.mobileInput.value = '';
      if (this.queryInput) this.queryInput.value = '';

      this.toggleResetButton();
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);

      // Focus the active input after clearing to trigger showSuggestions
      const activeInput = this.getActiveInput();
      activeInput?.focus();
    }
  }

  /**
   * Handles focus on the search input.
   * Triggers search if there's an existing query.
   */
  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      // Show suggestions when input is empty
      // If already showing suggestions/trending, don't re-render
      if (!this.isOpen || this.getAttribute("results") === "true") {
        this.showSuggestions();
      }
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  /**
   * Handles focus leaving the search component.
   * Closes the search results after a short delay.
   */
  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    }, 100); // Small delay to allow focus to move within component
  }

  /**
   * Handles keyup events for navigation and selection.
   * @param {KeyboardEvent} event - The keyup event
   */
  onKeyup(event) {
    switch (event.code) {
      case "Escape":
        this.close(false);
        event.preventDefault();
        break;
      case "ArrowUp":
        this.switchOption("up");
        event.preventDefault();
        break;
      case "ArrowDown":
        this.switchOption("down");
        event.preventDefault();
        break;
      default:
        // Only close if query is empty for other keys
        if (!this.getQuery().length) this.close(true);
    }
  }

  /**
   * Handles keydown events to prevent default cursor movement and Enter key handling.
   * @param {KeyboardEvent} event - The keydown event
   */
  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }

    // Handle Enter key
    if (event.code === "Enter") {
      const selectedOption = this.querySelector('[aria-selected="true"] a');
      if (selectedOption) {
        // Navigate to the selected option instead of submitting. A synthetic click reaches analytics pixels as a user click.
        event.preventDefault();
        window.location.assign(selectedOption.href);
      } else if (this.getQuery().length > 0) {
        // If there's a query but no selection, submit the form to show all results
        event.preventDefault();
        const form = this.querySelector('form');
        if (form) {
          // Sync the query input before submitting
          this.syncQueryInput();
          form.submit();
        }
      }
    }
  }

  /**
   * Switches between search result options using arrow keys.
   * @param {string} direction - The direction to move ('up' or 'down')
   */
  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    // Scoped to [role="option"]/.predictive-search__item so unrelated nested
    // <li> elements (e.g. a product card's own color-swatch list items)
    // never get treated as combobox options.
    const allVisibleElements = Array.from(
      this.querySelectorAll('li[role="option"], button.predictive-search__item')
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  /**
   * Fetches search results from the server with improved error handling and request deduplication.
   * @param {string} searchTerm - The term to search for
   * @async
   */
  async getSearchResults(searchTerm) {
    const queryKey = handleize(searchTerm);

    // Check for pending request
    if (this.pendingRequests.has(queryKey)) {
      return this.pendingRequests.get(queryKey);
    }

    // Check cache and validate timestamp
    if (this.cachedResults[queryKey]) {
      const cacheEntry = this.cachedResults[queryKey];
      if (Date.now() - cacheEntry.timestamp <= this.CACHE_EXPIRY_MS) {
        this.renderSearchResults(cacheEntry.markup);
        return;
      } else {
        this.cachedResults[queryKey] = undefined;
      }
    }

    // Abort any in-flight request
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.setLiveRegionLoadingState();

    // Create request promise
    const requestPromise = (async () => {
      try {
        // Early return if cached results exist
        if (this.cachedResults[queryKey]) {
          this.renderSearchResults(this.cachedResults[queryKey]);
          return;
        }
        // GC-2374: once Nosto has failed in this search box, or Nosto has been disabled
        // page-wide, skip straight to Shopify so we don't eat the 2s Nosto timeout on every
        // keystroke. Unlike the grids, predictive search keeps this fallback LOCAL (it does
        // not flip the global vendor — see fetchNostoResults()).
        const useNosto =
          String(window.theme?.data?.source).toLowerCase() === "nosto" &&
          !this.nostoFailedThisPage &&
          window.theme?.data?.nostoDisabled !== true;
        if (useNosto) {
          try {
            const results = await this.fetchNostoResults(searchTerm);
            this.cacheAndRenderResults(queryKey, results);
          } catch (nostoError) {
            if (nostoError.name === "AbortError") throw nostoError;
            // Nosto failed — remember it for this instance and fall back to Shopify locally.
            this.nostoFailedThisPage = true;
            if (this.allowDebug()) {
              console.warn("PredictiveSearch: Nosto failed, falling back to Shopify for this search box", nostoError);
            }
            const results = await this.fetchShopifyResults(searchTerm);
            this.cacheAndRenderResults(queryKey, results);
          }
        } else {
          const results = await this.fetchShopifyResults(searchTerm);
          this.cacheAndRenderResults(queryKey, results);
        }
      } catch (error) {
        this.handleSearchError(error, searchTerm);
      } finally {
        this.pendingRequests.delete(queryKey);
      }
    })();

    this.pendingRequests.set(queryKey, requestPromise);
    return requestPromise;
  }

  /**
   * Fetches search results from Shopify.
   * @param {string} searchTerm - The term to search for (or collection handle if context is 'trending')
   * @param {string} context - The context of the request: 'search' or 'trending' (default: 'search')
   * @returns {Promise<string|Object>} HTML markup for search results, or JSON object for trending
   * @private
   */
  async fetchShopifyResults(searchTerm, context = 'search') {
    let fetchUrl, sectionSelector, jsonSelector;

    if (context === 'trending') {
      // Fetch collection products in predictive search format
      const collectionHandle = this.trendingConfig.collectionHandle;
      const productsPerPage = this.trendingConfig.productsLimit;
      fetchUrl = `${window.location.origin}${window.theme.routes.collections_url}/${collectionHandle}?view=predictive-json&products_per_page=${productsPerPage}`;
      // Collection views return raw template output, not wrapped in a section
      sectionSelector = null;
      jsonSelector = "#collection-predictive-json";
    } else {
      // Standard predictive search
      const searchParams = new URLSearchParams({
        q: searchTerm,
        "resources[limit]": "10",
        "resources[type]": "product,query,collection",
        "resources[options][unavailable_products]": "hide",
        "resources[options][fields]":
          "body,product_type,tag,title,variants.title,vendor",
        section_id: "predictive-search-json",
      });
      fetchUrl = `${window.theme.routes.predictive_search_url}?${searchParams}`;
      sectionSelector = "#shopify-section-predictive-search-json";
      jsonSelector = "#predictive-search-json";
    }

    const response = await fetch(fetchUrl, {
      signal: this.abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`${context === 'trending' ? 'Collection' : 'Search'} request failed with status: ${response.status}`);
    }

    const text = await response.text();
    const parsedDoc = new DOMParser().parseFromString(text, "text/html");

    // Set search context: if there's a section selector, use it; otherwise search the whole document
    const searchContext = sectionSelector ? parsedDoc.querySelector(sectionSelector) : parsedDoc;
    const predictiveSearchJson = JSON.parse(
      searchContext?.querySelector(jsonSelector)?.textContent ?? "null"
    );

    if (this.allowDebug()) {
      console.log(`Shopify ${context} results:`, predictiveSearchJson);
    }

    // For trending, return the JSON data; for search, render and return HTML
    if (context === 'trending') {
      return predictiveSearchJson;
    }

    return this.renderSearchResultsFromJson(predictiveSearchJson);
  }

  /**
   * Fetches search results from Nosto using the utilities system.
   *
   * Builds a predictive search query, executes it through Nosto's API,
   * and transforms the response to match Shopify's predictive search format.
   * Uses a reduced timeout (2s) for faster fallback to Shopify.
   *
   * @param {string} searchTerm - The term to search for (unused if context is 'trending')
   * @param {string} context - The context of the request: 'search' or 'trending' (default: 'search')
   * @returns {Promise<string|Object>} HTML markup for search results, or JSON object for trending
   * @throws {Error} When Nosto search fails or times out
   * @private
   */
  async fetchNostoResults(searchTerm, context = 'search') {
    try {
      const { nostoBuildQuery, nostoSearch, nostoTransformToPredictiveSearch, nostoRecordSearchSubmit } =
        await loadNostoUtilities();

      let queryConfig;

      if (context === 'trending') {
        // Use collection context for trending products (like product-grid)
        queryConfig = nostoBuildQuery({
          context: 'collection',
          categoryId: this.trendingConfig.collectionId,
          type: 'products',
          size: this.trendingConfig.productsLimit,
          from: 0,
          filters: [],
          sortBy: 'relevance',
          allowSoldOutStateProducts: false
        });
      } else {
        // Standard predictive search
        queryConfig = nostoBuildQuery({
          context: "search",
          terms: searchTerm,
          type: "predictive",
          size: 10,
          from: 0,
          filters: [],
          sortBy: "relevance",
          allowSoldOutStateProducts: true,
        });
      }

      // Record search submit BEFORE the search request to mark as organic search
      // This is required for accurate Nosto analytics - see: https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
      if (searchTerm) {
        try {
          await nostoRecordSearchSubmit(searchTerm, { debug: this.allowDebug() });
        } catch (e) {
          // Non-blocking - don't let tracking errors affect search functionality
          if (this.allowDebug()) {
            console.error('Nosto recordSearchSubmit error:', e);
          }
        }
      }

      const response = await nostoSearch(
        queryConfig.query,
        queryConfig.options,
        {
          timeout: context === 'trending' ? 3000 : 2000,
          debug: this.allowDebug(),
          trigger: context === 'trending' ? 'predictive-search-trending' : 'predictive-search'
        }
      );

      if(this.allowDebug()) {
        console.log(`Nosto ${context} results:`, response);
      }

      const predictiveSearchJson = nostoTransformToPredictiveSearch(response);

      if(this.allowDebug()) {
        console.log(`Nosto to Shopify ${context} results:`, predictiveSearchJson);
      }

      // For trending, return the JSON data; for search, render and return HTML
      if (context === 'trending') {
        return predictiveSearchJson;
      }

      // Add search-users segment to visit for autocomplete searches
      nostojs(function(api){api.addSegmentCodeToVisit("search-users")});

      // Note: Search analytics are handled by the `track: "autocomplete"` option passed to api.search()
      // via nostoBuildQuery. Additionally, we call recordSearchSubmit above for organic search marking.
      // Click and add-to-cart events are tracked via dual attribution in ProductCard.
      // See: https://docs.nosto.com/techdocs/implementing-nosto/implement-search/search#analytics
      return this.renderSearchResultsFromJson(predictiveSearchJson);
    } catch (error) {
      // Log technical details for debugging while showing user-friendly message
      if (this.allowDebug()) {
        console.error("PredictiveSearch technical error details:", {
          code: error.code,
          userMessage: error.userMessage,
          debugMessage: error.debugMessage,
          originalError: error.originalError,
          details: error.details
        });
      } else {
        console.error("Nosto search error:", error);
      }
      // GC-2374: this method only runs when Nosto is the vendor, so any throw here (other than
      // a normal empty response, which resolves successfully) is a Nosto failure. We REPORT it
      // (emit datajet:nosto_failure) but deliberately do NOT call disableNostoForPage() — a
      // predictive-search failure uses short 2–3s timeouts, and a slow autocomplete shouldn't
      // flip the whole page (incl. the main collection grid) to Shopify. Predictive search falls
      // back locally instead: this method re-throws and the caller (getSearchResults /
      // loadTrendingProducts) catches it and retries via Shopify. The page-wide switch is
      // reserved for the grid/drawer/recs paths.
      if (window.theme?.utilities?.reportNostoFailure) {
        window.theme.utilities.reportNostoFailure(formatNostoErrorCode(error) || error.message, error.code);
      }

      // Fallback to empty results with a user-friendly message, but preserve the
      // enhanced Nosto error metadata so the visible ref line can be rendered
      // downstream (support screenshots carry the failure mode + lookup id).
      const surfacedError = new Error(error.userMessage || "Unable to load search results. Please try again.");
      surfacedError.code = error.code;
      surfacedError.stem = error.stem;
      surfacedError.details = error.details;
      surfacedError.debugMessage = error.debugMessage;
      surfacedError.errorCode = formatNostoErrorCode(error);
      throw surfacedError;
    }
  }

  /**
   * Caches and renders search results.
   * @param {string} queryKey - The cache key for the search results
   * @param {string} resultsMarkup - HTML markup for the search results
   * @private
   */
  cacheAndRenderResults(queryKey, resultsMarkup) {
    // Check if resultsMarkup is valid before proceeding
    if (!resultsMarkup) {
      // If resultsMarkup is undefined, the error was already handled in renderSearchResultsFromJson
      return;
    }

    const cacheEntry = {
      markup: resultsMarkup,
      timestamp: Date.now(),
    };

    this.allPredictiveSearchInstances.forEach((instance) => {
      instance.cachedResults[queryKey] = cacheEntry;
    });

    // Cleanup old cache entries
    this.cleanupCache();

    this.renderSearchResults(resultsMarkup);
  }

  /**
   * Load trending products from the configured collection.
   * Uses the same vendor system as ProductGrid (Shopify or Nosto).
   * Reuses existing fetch functions with 'trending' context.
   * Formats data and renders using the standard renderSearchResultsFromJson() pipeline.
   * @async
   * @private
   */
  async loadTrendingProducts() {
    if (!this.hasTrendingProducts()) {
      if (this.allowDebug()) {
        console.log('PredictiveSearch: loadTrendingProducts skipped - no config');
      }
      return this.trendingLoadPromise;
    }

    if (this.trendingConfig.isLoading || this.trendingConfig.isLoaded) {
      if (this.allowDebug()) {
        console.log('PredictiveSearch: loadTrendingProducts skipped - already loading/loaded');
      }
      return this.trendingLoadPromise;
    }

    if (this.allowDebug()) {
      console.log('PredictiveSearch: Starting to load trending products...');
    }

    this.trendingConfig.isLoading = true;

    this.trendingLoadPromise = (async () => {
      try {
        // GC-2374: use Nosto only if it's the vendor AND hasn't already failed in this box /
        // page-wide. On failure, fall back to Shopify trending locally (no page-wide switch).
        const useNosto =
          String(window.theme?.data?.source || 'shopify').toLowerCase() === 'nosto' &&
          !this.nostoFailedThisPage &&
          window.theme?.data?.nostoDisabled !== true;
        let predictiveSearchJson;

        if (this.allowDebug()) {
          console.log('PredictiveSearch: Loading trending from vendor:', useNosto ? 'nosto' : 'shopify');
        }

        // Reuse existing fetch functions with 'trending' context
        // Both return JSON data in predictive search format
        if (useNosto) {
          try {
            predictiveSearchJson = await this.fetchNostoResults('', 'trending');
          } catch (nostoError) {
            if (nostoError.name === 'AbortError') throw nostoError;
            this.nostoFailedThisPage = true;
            if (this.allowDebug()) {
              console.warn('PredictiveSearch: Nosto trending failed, falling back to Shopify', nostoError);
            }
            predictiveSearchJson = await this.fetchShopifyResults('', 'trending');
          }
        } else {
          predictiveSearchJson = await this.fetchShopifyResults('', 'trending');
        }

        // Store trending data for later use when showing suggestions
        if (predictiveSearchJson?.resources?.products) {
          this.trendingData = predictiveSearchJson;
          this.trendingConfig.isLoaded = true;
          if (this.allowDebug()) {
            console.log('PredictiveSearch: Trending products loaded:', predictiveSearchJson.resources.products.length);
          }
        } else if (this.allowDebug()) {
          console.log('PredictiveSearch: No products in trending response');
        }
      } catch (error) {
        console.error('Error loading trending products:', error);
        this.trendingData = null;
      } finally {
        this.trendingConfig.isLoading = false;
      }
    })();

    return this.trendingLoadPromise;
  }


  /**
   * Handle vendor change events from theme utilities
   * @param {CustomEvent} event
   * @private
   */
  handleVendorChange(event) {
    if (this.allowDebug()) {
      console.log("PredictiveSearch handleVendorChange", event);
    }

    const { vendor } = event.detail;

    if (this.allowDebug()) {
      console.log(`PredictiveSearch: Search vendor changed to ${vendor}, clearing cache...`);
    }

    // Clear all cached results since they're from the old vendor
    this.cachedResults = {};

    // Clear pending requests
    this.pendingRequests.clear();

    // Abort any in-flight request
    this.abortController?.abort();
    this.abortController = new AbortController();

    // Clear trending data and reload with new vendor
    this.trendingData = null;
    this.trendingConfig.isLoaded = false;
    this.trendingLoadPromise = null;
    if (this.hasTrendingProducts()) {
      this.loadTrendingProducts();
    }

    // If there's an active search term, re-run the search with the new vendor
    if (this.searchTerm && this.searchTerm.trim().length > 0) {
      if (this.allowDebug()) {
        console.log(`PredictiveSearch: Re-running search for "${this.searchTerm}" with new vendor`);
      }

      // Clear the current timeout and start a new search immediately
      clearTimeout(this.searchTimeout);
      this.getSearchResults(this.searchTerm);
    }
  }

  /**
   * Cleans up expired cache entries.
   * @private
   */
  cleanupCache() {
    const now = Date.now();
    Object.keys(this.cachedResults).forEach((queryKey) => {
      if (now - this.cachedResults[queryKey].timestamp > this.CACHE_EXPIRY_MS) {
        this.cachedResults[queryKey] = undefined;
      }
    });
  }

  /**
   * Handles search errors with appropriate error messages.
   * @param {Error} error - The error that occurred
   * @param {string} searchTerm - The search term that caused the error
   * @private
   */
  handleSearchError(error, searchTerm) {
    if (error.name === "AbortError") {
      return; // Request was aborted, no need to show error
    }

    let errorMessage = "An error occurred. Please try again.";
    if (error.name === "TimeoutError") {
      errorMessage = "Search timed out. Please try again.";
    } else if (error.message.includes("HTTP error")) {
      errorMessage = "Unable to connect to search service. Please try again.";
    }

    // errorCode is attached upstream from the Nosto utilities; fall back to
    // formatting here in case a raw enhanced error reaches this handler.
    const ref = error.errorCode || formatNostoErrorCode(error);
    this.renderErrorDisplay(searchTerm, errorMessage, ref);
    console.error("Search error:", error);
  }

  /**
   * Renders search results from JSON data.
   * @param {Object} data - The search results data
   * @returns {string} HTML markup for the search results
   */
  async renderSearchResultsFromJson(data) {
    if (
      !data ||
      !data.performed ||
      !data.resources ||
      Object.keys(data).length === 0
    ) {
      // Handle error case directly without returning undefined
      this.renderErrorDisplay();
      return; // Return early, renderErrorDisplay handles DOM updates
    }

    // Set vendor data attribute from the rendering data
    this.dataset.vendor = data.vendor;

    const resources = data.resources;
    const firstColumnResultsSize =
      (resources.queries?.length || 0) +
      (resources.collections?.length || 0) +
      (resources.pages?.length || 0) +
      (resources.articles?.length || 0);

    // Check if there are no results at all
    if (firstColumnResultsSize === 0 && (resources.products?.length || 0) === 0) {
      this.renderErrorDisplay(data.terms || this.searchTerm);
      return; // Return early, renderErrorDisplay handles DOM updates
    }

    let resultsMarkup = '<div id="predictive-search-results" role="listbox">';

    if (firstColumnResultsSize > 0 || (resources.products?.length || 0) > 0) {
      resultsMarkup +=
        '<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper' +
        ((resources.products?.length || 0) === 0
          ? " predictive-search__results-groups-wrapper--no-products"
          : "") +
        ((resources.queries?.length || 0) === 0 &&
        (resources.collections?.length || 0) === 0
          ? " predictive-search__results-groups-wrapper--no-suggestions"
          : "") +
        '">';

      if (firstColumnResultsSize > 0) {
        resultsMarkup += '<div class="predictive-search__result-group">';

        if (
          (resources.queries?.length || 0) > 0 ||
          (resources.collections?.length || 0) > 0
        ) {
          resultsMarkup +=
            '<div class="predictive-search__results-suggestions">';

          if ((resources.queries?.length || 0) > 0) {
            resultsMarkup += `
              <div class="predictive-search__results-queries-container">
                <h2 id="predictive-search-queries" class="predictive-search__heading">Related Search</h2>
                <ul
                  id="predictive-search-results-queries-list"
                  class="predictive-search__results-list search__suggestion-list"
                  role="group"
                  aria-labelledby="predictive-search-queries"
                >
            `;

            resources.queries?.forEach((query, index) => {
              const suggestionUrl = buildSuggestionUrl(query);
              let itemHandle = suggestionUrl.split('/').pop().split('?')[0].split('&')[0].split('=')[0];
              // if the suggestion url is a search url, then the item handle should be the search term
              // example: /search?q=polo%20with%20contrast&is_search_suggestion=true -> polo-with-contrast
              if (suggestionUrl.includes('/search')) {
                itemHandle = handleize(decodeURIComponent(suggestionUrl.split('?')[1].split('&')[0].split('=')[1]));
              }
              resultsMarkup += `
                <li
                  id="predictive-search-option-query-${index + 1}"
                  class="predictive-search__list-item"
                  role="option"
                  aria-selected="false"
                >
                  <a
                    id="search-suggestion-${itemHandle}"
                    href="${suggestionUrl}"
                    class=""
                  >
                    <div
                      class="predictive-search__item-content predictive-search__item-content--centered"
                    >
                      <p
                        class="predictive-search__item-heading predictive-search__item-query-result"
                        aria-label="${query.text}"
                      >
                        ${query.styledText}
                      </p>
                    </div>
                  </a>
                </li>
              `;
            });

            resultsMarkup += "</ul></div>";
          }

          if ((resources.collections?.length || 0) > 0) {
            resultsMarkup += `
              <div class="predictive-search__results-collections-container">
                <h2 id="predictive-search-categories" class="predictive-search__heading">Categories</h2>
                <ul
                  id="predictive-search-results-categories-list"
                  class="predictive-search__results-list search__suggestion-list"
                  role="group"
                  aria-labelledby="predictive-search-categories"
                >
            `;

            resources.collections.forEach((collection, index) => {
              const suggestionUrl = buildSuggestionUrl(collection);
              const itemHandle = suggestionUrl.split('/').pop().split('?')[0].split('&')[0].split('=')[0];
              resultsMarkup += `
                <li
                  id="predictive-search-option-collection-${index + 1}"
                  class="predictive-search__list-item"
                  role="option"
                  aria-selected="false"
                >
                  <a
                    id="search-suggestion-${itemHandle}"
                    href="${suggestionUrl}"
                    class=""
                  >
                    <div class="predictive-search__item-content predictive-search__item-content--centered">
                      <p class="predictive-search__item-heading">
                        ${collection.title}
                      </p>
                    </div>
                  </a>
                </li>
              `;
            });

            resultsMarkup += "</ul></div>";
          }

          resultsMarkup += "</div>";
        }

        // Add mobile-only Show All Results link after Related Searches
        const searchQuery = data.terms || this.searchTerm;
        const searchUrl = `${window.theme.routes.search_url}?q=${encodeURIComponent(searchQuery)}&type=product`;

        resultsMarkup += `
          <div class="predictive-search__mobile-view-all">
            <a
              id="predictive-search-show-all-results-mobile"
              href="${searchUrl}"
              class="predictive-search__view-all"
            >
              Show All Results
            </a>
          </div>
        `;

        resultsMarkup += "</div>";
      }

      if (
        resources.products.length > 0 ||
        resources.articles.length > 0 ||
        resources.pages.length > 0
      ) {
        resultsMarkup += '<div class="predictive-search__result-group">';

        if (resources.products.length > 0) {
          const searchQuery = data.terms || this.searchTerm;
          const searchUrl = `${window.theme.routes.search_url}?q=${encodeURIComponent(searchQuery)}&type=product`;

          // Use custom heading for trending, otherwise use "Top Results"
          const heading = data.headingOverride || 'Top Results';
          // Hide "Show All Results" link for trending products
          const showViewAllLink = !data.isTrending;

          resultsMarkup += `
            <div class="predictive-search__products-wrapper">
              <div class="predictive-search__header">
                <h2 id="predictive-search-products" class="predictive-search__heading">${heading}</h2>
                ${showViewAllLink ? `<a id="predictive-search-show-all-results-desktop" href="${searchUrl}" class="predictive-search__view-all">Show All Results</a>` : ''}
              </div>
              <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
          `;

          // Fetch all product cards first
          // only process up to this.productLimit
          const limitedProducts = resources.products.slice(0, this.productLimit);
          const productCards = await Promise.all(
            limitedProducts.map(async (product, index) => {
              try {
                const productElement = this.createProductElement(product, data.vendor);
                if (productElement) {
                  if(this.allowDebug()) {
                    console.log('PredictiveSearch.renderSearchResultsFromJson', productElement, productElement.outerHTML, product);
                  }
                  return `
                  <li
                    id="predictive-search-option-product-${index + 1}"
                    class="predictive-search__list-item"
                    role="option"
                    aria-selected="false"
                  >
                    ${productElement.outerHTML}
                  </li>`;

                } else {
                  console.warn(`Failed to generate product card for ${product.url}:`, productElement, product);
                  return '';
                }
              } catch (error) {
                console.error(
                  `Failed to generate product card for ${product.url}:`,
                  error
                );
                return '';
              }
            })
          );

          // Add all product cards to the markup
          resultsMarkup += productCards.join("");
          resultsMarkup += "</ul></div>";
        }

        if (resources.articles.length > 0 || resources.pages.length > 0) {
          resultsMarkup += `
            <div class="predictive-search__pages-wrapper">
              <h2 id="predictive-search-pages-mobile" class="predictive-search__heading text-body caption-with-letter-spacing">Pages</h2>
              <ul id="predictive-search-results-pages-list-mobile" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-pages-mobile">
          `;

          resources.pages.forEach((page, index) => {
            const suggestionUrl = buildSuggestionUrl(page);
            const itemHandle = suggestionUrl.split('/').pop().split('?')[0].split('&')[0].split('=')[0];
            resultsMarkup += `
              <li
                id="predictive-search-option-page-mobile-${index + 1}"
                class="predictive-search__list-item"
                role="option"
                aria-selected="false"
              >
                <a
                  id="search-suggestion-${itemHandle}"
                  href="${suggestionUrl}"
                  class="predictive-search__item link link--text"
                >
                  <div class="predictive-search__item-content predictive-search__item-content--centered">
                    <p class="predictive-search__item-heading h5">${
                      page.title
                    }</p>
                  </div>
                </a>
              </li>
            `;
          });

          resources.articles.forEach((article, index) => {
            const suggestionUrl = buildSuggestionUrl(article);
            const itemHandle = suggestionUrl.split('/').pop().split('?')[0].split('&')[0].split('=')[0];
            resultsMarkup += `
              <li
                id="predictive-search-option-article-mobile-${index + 1}"
                class="predictive-search__list-item"
                role="option"
                aria-selected="false"
              >
                <a
                  id="search-suggestion-${itemHandle}"
                  href="${suggestionUrl}"
                  class="predictive-search__item link link--text"
                >
                  <div class="predictive-search__item-content predictive-search__item-content--centered">
                    <p class="predictive-search__item-heading h5">
                      ${article.title}
                    </p>
                  </div>
                </a>
              </li>
            `;
          });

          resultsMarkup += "</ul></div>";
        }

        resultsMarkup += "</div>";
      }

      resultsMarkup += "</div>";
    }

    resultsMarkup += `</div>`;

    return resultsMarkup;
  }

  /**
   * Create product element from data
   * @param {Object} product
   * @param {string} vendor - The vendor of the product
   * @returns {HTMLElement|null}
   * @private
   */
  createProductElement(product, vendor) {
    // Try to use product-card component first
    const productCardElement = this.createProductCardComponent(product, vendor);
    if (productCardElement) {
      return productCardElement;
    }
    console.warn(`Failed to create product element for ${product.url}:`, product);
    return null;
  }

  /**
   * Create product card component from data
   * @param {Object} product
   * @param {string} vendor - The vendor of the product
   * @returns {HTMLElement|null}
   * @private
   */
  createProductCardComponent(product, vendor) {
    const template = this.productCardComponentTemplate;
    if (!template) {
      console.warn(`Failed to find product card component template:`, template, product);
      return null;
    }

    const clone = template.content.cloneNode(true);
    const productCardElement = clone.querySelector("product-card");

    if (!productCardElement) {
      console.warn(`Failed to find product card element in template:`, template, product);
      return null;
    }

    // Set up the product card component data attributes
    const defaultVariantId = product.defaultVariantId || null;
    const fetchUrl = this.buildProductFetchUrl(product, defaultVariantId);
    const productUrl = product.url;

    // Generate unique component ID for heatmap tracking
    // Format: product-card-{product.id}__predictive-search
    const componentId = `predictive-search-product-card-${product.id}`;

    // Set data attributes for client-side rendering
    productCardElement.id = componentId;
    productCardElement.dataset.productId = product.id;
    productCardElement.dataset.fetchUrl = fetchUrl;
    productCardElement.dataset.productUrl = productUrl;
    productCardElement.dataset.defaultVariantId = defaultVariantId;
    productCardElement.dataset.productFormId = `quick-add-${this.id}-${product.id}`;
    productCardElement.classList.add("lazy-load");
    productCardElement.dataset.vendor = vendor;

    // Set Nosto context for attribution tracking
    if (vendor === "nosto") {
      productCardElement.dataset.nostoContext = "predictive-search";
    }

    return productCardElement;
  }

  /**
   * Build product fetch URL for client-side rendering
   * @param {Object} product
   * @param {number|string|null} [defaultVariantId] - Variant to select in the product-json response.
   *   Without this, Shopify's product-json view has no `selected_variant`, and product-card.js
   *   removes the card (setDefaultVariant() requires selected_variant).
   * @returns {string}
   * @private
   */
  buildProductFetchUrl(product, defaultVariantId) {
    // For client-side rendering, we'll use the product URL with the product-json view parameter
    // Handle cases where the product URL already contains query parameters
    const url = new URL(product.url, window.location.origin);
    if (defaultVariantId) {
      url.searchParams.set("variant", defaultVariantId);
    }
    url.searchParams.set("view", "product-json");
    return url.toString();
  }

  /**
   * Renders an error display when no results are found.
   */
  renderErrorDisplay(searchTerm = "", message = "", ref = "") {
    // Compact diagnostic ref, surfaced on-screen so support screenshots carry
    // the failure mode (library/timeout/network/api), timing, and a lookup id.
    const refMarkup = ref
      ? `<span class="predictive-search__error-ref" data-error-ref>${ref}</span>`
      : "";

    const errorMarkup = `
      <div class="predictive-search__error">
        <p>
          ${
            message ||
            `No results found${
              searchTerm ? ` for "${searchTerm}"` : ""
            }. Check the spelling or use a different word or phrase.`
          }
        </p>
        ${refMarkup}
      </div>
    `;
    this.predictiveSearchResults.innerHTML = errorMarkup;

    // Add search class for error state
    this.predictiveSearchResults.classList.remove('predictive-search__results--trending');
    this.predictiveSearchResults.classList.add('predictive-search__results--search');

    this.setAttribute("results", true);
    this.setLiveRegionResults();
  }

  /**
   * Sets the loading state for the live region.
   */
  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  /**
   * Updates the text in the live region.
   * @param {string} statusText - The text to display
   */
  setLiveRegionText(statusText) {
    this.statusElement.textContent = statusText;
  }

  /**
   * Renders the search results in the UI.
   * @param {string} resultsMarkup - HTML markup for the results
   * @param {boolean} isSearchResults - Whether this is actual search results (vs trending)
   */
  renderSearchResults(resultsMarkup, isSearchResults = true) {
    // Safety check for undefined resultsMarkup
    if (!resultsMarkup || typeof resultsMarkup !== 'string') {
      console.error('Invalid resultsMarkup provided to renderSearchResults:', resultsMarkup);
      return;
    }

    // Create a document fragment to minimize reflows
    const template = document.createElement("template");
    template.innerHTML = resultsMarkup.trim();

    // Clear existing content
    while (this.predictiveSearchResults.firstChild) {
      this.predictiveSearchResults.removeChild(
        this.predictiveSearchResults.firstChild
      );
    }

    // Append new content
    this.predictiveSearchResults.appendChild(template.content);

    // Add class to distinguish between trending and search results
    if (isSearchResults) {
      this.predictiveSearchResults.classList.remove('predictive-search__results--trending');
      this.predictiveSearchResults.classList.add('predictive-search__results--search');
    } else {
      this.predictiveSearchResults.classList.remove('predictive-search__results--search');
      this.predictiveSearchResults.classList.add('predictive-search__results--trending');
    }

    // Only set "results" attribute for actual search results, not trending
    // This prevents hiding suggestions when trending products are shown
    if (isSearchResults) {
      this.setAttribute("results", true);
      this.setLiveRegionResults();
    } else {
      this.removeAttribute("results");
    }

    // Calculate dimensions before opening
    this.resultsMaxHeight = this.getResultsMaxHeight();
    this.open();
  }

  /**
   * Updates the live region with the number of results.
   */
  setLiveRegionResults() {
    this.removeAttribute("loading");
    const count = this.predictiveSearchResults.querySelectorAll(
      "#predictive-search-results-products-list .predictive-search__list-item"
    ).length;
    const template =
      count === 1
        ? this.getAttribute("data-results-count-singular")
        : this.getAttribute("data-results-count-plural");
    const resultsCountText = template
      ? template.replace(/\d+/, count)
      : "";
    this.setLiveRegionText(resultsCountText);
  }

  /**
   * Calculates the maximum height for the results container.
   * @returns {number} The maximum height in pixels
   */
  getResultsMaxHeight() {
    const headerHeight =
      document.querySelector(".section-header")?.getBoundingClientRect()
        ?.bottom ?? 0;
    const modalContentHeight =
      document.querySelector(".search-modal.modal__content")?.offsetHeight ?? 0;
    this.resultsMaxHeight =
      window.innerHeight - headerHeight - modalContentHeight;
    return this.resultsMaxHeight;
  }

  /**
   * Shows suggestions when input is empty or focused.
   * If trending products are configured and loaded, renders them using the standard pipeline.
   */
  async showSuggestions() {
    // If trending is currently loading, wait for it to complete
    if (this.trendingLoadPromise && !this.trendingConfig.isLoaded) {
      if (this.allowDebug()) {
        console.log('PredictiveSearch: Waiting for trending to load...');
      }
      await this.trendingLoadPromise;
      if (this.allowDebug()) {
        console.log('PredictiveSearch: Trending loaded', this.trendingData);
      }
    }

    // If we have trending data, render it using the standard pipeline
    if (this.trendingData && this.hasTrendingProducts()) {
      // Create a custom results object with trending products
      // Override the heading to show "Trending Now" instead of "Top Results"
      const trendingResultsJson = {
        ...this.trendingData,
        isTrending: true, // Flag to identify this as trending data
        headingOverride: this.trendingConfig.heading
      };

      // Render using standard pipeline
      // Pass isSearchResults=false so suggestions stay visible
      const resultsMarkup = await this.renderSearchResultsFromJson(trendingResultsJson);
      if (resultsMarkup) {
        this.renderSearchResults(resultsMarkup, false); // false = not search results, don't hide suggestions
        return;
      }
    }

    // If no trending data, ensure we're in suggestions mode
    this.removeAttribute("results");

    // Just open to show suggestions
    this.open();
  }

  /**
   * Opens the search results dropdown.
   */
  open() {
    if (this.isOpen) return; // Already open

    // Batch style changes
    requestAnimationFrame(() => {
      this.setAttribute("open", true);
      this.input.setAttribute("aria-expanded", true);
    });
  }

  /**
   * Closes the search results dropdown.
   * @param {boolean} [clearSearchTerm=false] - Whether to clear the search input
   */
  close(clearSearchTerm = false) {
    if (!this.isOpen) return; // Already closed
    this.closeResults(clearSearchTerm);
  }

  /**
   * Closes the search results and resets the UI state.
   * @param {boolean} [clearSearchTerm=false] - Whether to clear the search input
   */
  closeResults(clearSearchTerm = false) {
    // Batch operations
    requestAnimationFrame(() => {
      if (clearSearchTerm) {
        this.input.value = "";
        this.removeAttribute("results");
      }

      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute("aria-selected", false);

      // Clear result type classes
      this.predictiveSearchResults.classList.remove('predictive-search__results--search');
      this.predictiveSearchResults.classList.remove('predictive-search__results--trending');

      // Batch attribute changes
      this.input.setAttribute("aria-activedescendant", "");
      this.input.setAttribute("aria-expanded", false);
      this.removeAttribute("loading");
      this.removeAttribute("open");

      // Style changes last
      this.resultsMaxHeight = false;
      this.predictiveSearchResults.removeAttribute("style");
    });
  }
}

customElements.define("predictive-search", PredictiveSearch);

/**
 * @classdesc A custom element that displays popular and recommended search suggestions.
 * @extends HTMLElement
 */
if (!customElements.get("predictive-search-suggestions")) {
  class PredictiveSearchSuggestions extends HTMLElement {
    /**
     * Creates a new PredictiveSearchSuggestions instance.
     * @constructor
     */
    constructor() {
      super();
      this.suggestions = [];
    }

    /**
     * Called when the element is connected to the DOM.
     * Initializes and renders the suggestions.
     */
    connectedCallback() {
      this.suggestions = window.theme?.search?.suggestions || [];
      this.renderSuggestions();
    }

    /**
     * Builds a suggestion URL with tracking parameter.
   * @param {Object} item - The suggestion item with title and url
   * @returns {string} The formatted URL
   */
  buildSuggestionUrl(item) {
    return buildSuggestionUrl(item);
  }

    /**
     * Renders all suggestion groups.
     */
    renderSuggestions() {
      this.suggestions.forEach((group, index) => {
        if (!group.items || group.items.length === 0) return;

        const groupContainer = document.createElement("div");
        groupContainer.classList.add("search__suggestion-list__container");

        if (group.heading) {
          groupContainer.innerHTML = `<h2 class="predictive-search__heading">${group.heading}</h2>`;
        }

        const listContainer = document.createElement("ul");
        listContainer.classList.add(
          "search__suggestion-list",
          `search__suggestion-group-${index}`
        );

        group.items.forEach((item) => {
          const suggestionUrl = this.buildSuggestionUrl(item);
          let itemHandle = suggestionUrl.split('/').pop().split('?')[0].split('&')[0].split('=')[0];
          // if the suggestion url is a search url, then the item handle should be the search term
          // example: /search?q=polo%20with%20contrast&is_search_suggestion=true -> polo-with-contrast
          if (suggestionUrl.includes('/search')) {
            itemHandle = handleize(decodeURIComponent(suggestionUrl.split('?')[1].split('&')[0].split('=')[1]));
          }
          listContainer.innerHTML += `
            <li>
              <a
                id="search-suggestion-${itemHandle}"
                href="${suggestionUrl}"
              >
                ${item.title}
              </a>
            </li>
          `;
        });

        groupContainer.appendChild(listContainer);
        this.appendChild(groupContainer);
      });
    }
  }

  customElements.define(
    "predictive-search-suggestions",
    PredictiveSearchSuggestions
  );
}
